import { IDataWithLoader } from '@/types/index';
import actions from '@/store/dashboardAction';
import { GetterTree, MutationTree } from 'vuex';
import {
  ICountData,
  IDashboardState,
  TResponseCountData,
} from '@/types/dashboard';
import { RootState } from '@/types';

const state: IDashboardState = {
  runningCampaignsCount: {
    loadData: {
      loader: false,
      controller: null,
    },
    data: 0,
  },
  spendCount: {
    loadData: {
      loader: false,
      controller: null,
    },
    data: {
      today: 0,
      yesterday: 0,
    },
  },
  impressionsCount: {
    loadData: {
      loader: false,
      controller: null,
    },
    data: {
      today: 0,
      yesterday: 0,
    },
  },
  conversionsCount: {
    loadData: {
      loader: false,
      controller: null,
    },
    data: {
      today: 0,
      yesterday: 0,
    },
  },
  chart: {
    loadData: {
      loader: false,
      controller: null,
    },
    data: [],
  },
  sspTable: {
    loadData: {
      loader: false,
      controller: null,
    },
    data: [],
  },
  siteDomainsTable: {
    loadData: {
      loader: false,
      controller: null,
    },
    data: [],
  },
  pieChart: {
    loadData: {
      loader: false,
      controller: null,
    },
    data: [],
  },
};

const mutations: MutationTree<IDashboardState> = {
  setRunningCampaignsCount(state, response) {
    if (!response) return;

    state.runningCampaignsCount.data = response.data.length;
    state.runningCampaignsCount.loadData.loader = false;
  },
  setSpendCount(state, response: TResponseCountData) {
    if (!response) return;

    const [today, yesterday] = response;

    if (today) {
      state.spendCount.data.today = today;
    }
    if (yesterday) {
      state.spendCount.data.yesterday = yesterday;
    }
    state.spendCount.loadData.loader = false;
  },
  setImpressionsCount(state, response: TResponseCountData) {
    if (!response) return;

    const [today, yesterday] = response;

    if (today) {
      state.impressionsCount.data.today = today;
    }
    if (yesterday) {
      state.impressionsCount.data.yesterday = yesterday;
    }
    state.impressionsCount.loadData.loader = false;
  },
  setConversionsCount(state, response: TResponseCountData) {
    if (!response) return;

    const [today, yesterday] = response;

    if (today) {
      state.conversionsCount.data.today = today;
    }
    if (yesterday) {
      state.conversionsCount.data.yesterday = yesterday;
    }
    state.conversionsCount.loadData.loader = false;
  },
  setChart(state, data: Array<any>) {
    if (!data) return;

    state.chart.data = data;
    state.chart.loadData.loader = false;
  },
  setSspTable(state, data: Array<any>) {
    if (!data) return;

    state.sspTable.data = data;
    state.sspTable.loadData.loader = false;
  },
  setSiteDomainsTable(state, data: Array<any>) {
    if (!data) return;

    state.siteDomainsTable.data = data;
    state.siteDomainsTable.loadData.loader = false;
  },
  setPieChart(state, data: Array<any>) {
    if (!data) return;

    state.pieChart.data = data;
    state.pieChart.loadData.loader = false;
  },
  changeLoader(state, [name, value]: [string, boolean]) {
    if (state[name]) {
      state[name].loadData.loader = value;
    }
  },
  changeController(state, [name, value]: [string, AbortController]) {
    if (state[name]) {
      state[name].loadData.controller = value;
    }
  },
};

const getters: GetterTree<IDashboardState, RootState> = {
  getRunningCampaignsCount(state): IDataWithLoader<number> {
    return state.runningCampaignsCount;
  },
  getSpendCount(state): IDataWithLoader<ICountData> {
    return state.spendCount;
  },
  getImpressionsCount(state): IDataWithLoader<ICountData> {
    return state.impressionsCount;
  },
  getConversionsCount(state): IDataWithLoader<ICountData> {
    return state.conversionsCount;
  },
  getChart(state): Array<any> {
    return state.chart.data;
  },
  getSspTable(state): Array<any> {
    return state.sspTable.data;
  },
  getSiteDomainsTable(state): Array<any> {
    return state.siteDomainsTable.data;
  },
  getPieChart(state): Array<any> {
    return state.pieChart.data;
  },
  getLoader:
    (state) =>
    (name): boolean =>
      state[name]?.loadData.loader ?? false,
  getController:
    (state) =>
    (name): AbortController | null =>
      state[name]?.loadData.controller ?? null,
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
